import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Rocket, X, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useOnboarding } from '../hooks/useOnboarding';
import { useProperties } from '../hooks/useManagement';

interface ConnectStatus { connected: boolean; charges_enabled: boolean }

const TOTAL_STEPS = 5;

// Floating "continue onboarding" nudge. Appears bottom-center on any portal page
// while the alta isn't finished, to keep the new customer focused. Uses the SAME
// 5-step completion model as the dashboard (agreement → pay → details → properties
// → Stripe), so it never claims "done" while the dashboard shows pending steps.
// Hidden once complete, on the onboarding pages themselves, or when dismissed.
export function OnboardingNudge() {
  const { data } = useOnboarding();
  const { data: properties } = useProperties();
  const { data: connect } = useQuery({
    queryKey: ['connect-status'],
    queryFn: async (): Promise<ConnectStatus> => {
      const res = await fetch('/api/portal/connect/status', { credentials: 'same-origin' });
      if (!res.ok) return { connected: false, charges_enabled: false };
      return res.json();
    },
  });
  const location = useLocation();
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);

  if (!data || dismissed) return null;

  const signed = data.agreement.signed;
  const paid = data.payment.status === 'paid';
  const submitted = data.profile.status === 'submitted';
  const hasProperties = (properties?.length ?? 0) > 0;
  const chargesEnabled = connect?.charges_enabled ?? false;
  const done = [signed, paid, submitted, hasProperties, chargesEnabled].filter(Boolean).length;
  if (done === TOTAL_STEPS) return null; // onboarding complete

  const onOnboardingPage = ['/portal/dashboard', '/portal/agreement', '/portal/payment', '/portal/onboarding']
    .some(p => location.pathname.startsWith(p));
  if (onOnboardingPage) return null;

  const next = !signed
    ? { label: t('portal.nudge.sign', 'Firma tu acuerdo'), to: '/portal/agreement' }
    : !paid
      ? { label: t('portal.nudge.pay', 'Elige tu plan y paga'), to: '/portal/payment' }
      : !submitted
        ? { label: t('portal.nudge.details', 'Sube los detalles de tu alojamiento'), to: '/portal/onboarding' }
        : !hasProperties
          ? { label: t('portal.nudge.properties', 'Añade tus alojamientos'), to: '/portal/manage/properties' }
          : { label: t('portal.nudge.payouts', 'Conecta tus cobros (Stripe)'), to: '/portal/manage/payouts' };

  return (
    <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 w-[92%] max-w-md print:hidden">
      <div className="rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 p-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 text-white flex items-center justify-center shrink-0">
            <Rocket size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-slate-900">
              {t('portal.nudge.title', 'Completa tu alta')} · {done}/{TOTAL_STEPS}
            </p>
            <p className="text-xs text-slate-500 truncate">{t('portal.nudge.next', 'Siguiente')}: {next.label}</p>
          </div>
          <button onClick={() => setDismissed(true)} aria-label="Cerrar" className="text-slate-400 hover:text-slate-600 shrink-0">
            <X size={16} />
          </button>
        </div>
        <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all" style={{ width: `${(done / TOTAL_STEPS) * 100}%` }} />
        </div>
        <Link
          to={next.to}
          className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold transition-colors"
        >
          {t('portal.nudge.continue', 'Continuar mi alta')} <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}
